import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { ArrowLeftIcon, ChartBarIcon, DocumentTextIcon } from '@heroicons/react/20/solid'
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/16/solid'
import { Select } from 'components/select'
import { MultiSelect } from 'components/multi-select'
import { Input } from 'components/input'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import loadingImage from '../../assets/images/boat-loading.gif'
import Flatpickr from 'react-flatpickr'
import { useLazyQuery } from '@apollo/client'
import { SEARCH_LOCATION, GET_EXPLORE_ITEMS_CLICKS_REPORT } from 'graphql/reports/queries'
import { GET_COUNTRY_LIST } from 'graphql/common/queries'
import moment from 'moment'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import i18next from 'i18next'
import { BuildingLibraryIcon } from '@heroicons/react/24/outline'
import { GET_EXPLORE_ITEMS } from 'graphql/exploreItems/queries'

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100]

const CATEGORY_OPTIONS = [
    { value: 'ATR', label: 'Attraction' },
    { value: 'HOT', label: 'Hotel' },
    { value: 'CAR', label: 'Car Rental' },
]

const INITIAL_FILTER_STATE = {
    start_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
    end_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
    country_code: [],
    item_Category: [],
    item_id: [],
}

export default function AttractionsClicksReport() {
    const navigate = useNavigate()
    const { t } = useTranslation()

    // States
    const [filterObj, setFilterObj] = useState(INITIAL_FILTER_STATE)
    const [data, setData] = useState([])
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(10)
    const [currentPage, setCurrentPage] = useState(1)
    const [exportLoading, setExportLoading] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [filterApplied, setFilterApplied] = useState(false)
    const [countries, setCountries] = useState([])
    const [routes, setRoutes] = useState([])
    const [trips, setTrips] = useState([])
    const [tripStatuses, setTripStatuses] = useState([])
    const [totalPassengers, setTotalPassengers] = useState(0)
    const [availableCategories, setAvailableCategories] = useState(CATEGORY_OPTIONS)
    const [availableItems, setAvailableItems] = useState(0)

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setData([])
            setFilterApplied(false)
            setTotalPassengers(0)
        }
    }, [])

    // GraphQL query
    const [getClicksReport, { loading: reportLoading }] = useLazyQuery(GET_EXPLORE_ITEMS_CLICKS_REPORT, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.userItemClickReport.success && data.userItemClickReport.code === '200') {
                setData(data.userItemClickReport.user_item_click_report)
                setTotalRows(data.userItemClickReport.total_count)
            } else {
                handleError(data.userItemClickReport.message)
            }
        },
        onError: error => handleError(error.message),
    })

    const [getCountries] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.country) {
                setCountries(data.country)
            }
        },
        onError: error => handleError(error.message),
    })

    const handleError = message => {
        setDialogMessage(message || t('Failed to fetch nationality report'))
        setShowErrDialog(true)
    }

    useEffect(() => {
        getCountries({ variables: { code: '' } })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Run once on mount - lazy queries are stable

    const fetchReport = (page = 1) => {
        const offset = (page - 1) * perPage
        getClicksReport({
            variables: {
                start_date: filterObj?.start_date,
                end_date: filterObj?.end_date,
                page: {
                    limit: perPage,
                    offset,
                },
                item_category: filterObj.item_category || null,
                item_id: filterObj?.item_id > 0 ? filterObj.item_id : null,
                countryCode: filterObj.country_code || null,
            },
        })
    }

    useEffect(() => {
        if (filterApplied) {
            fetchReport(currentPage)
        }
    }, [currentPage, perPage, filterApplied])

    const handlePageChange = page => {
        if (filterApplied) {
            setCurrentPage(page)
        }
    }

    const handlePerRowsChange = newPerPage => {
        if (newPerPage !== perPage && filterApplied) {
            setPerPage(newPerPage)
            setCurrentPage(1)
        }
    }

    const handleApplyFilter = () => {
        setCurrentPage(1)
        setFilterApplied(true)
        fetchReport(1)
    }

    const handleClearFilter = () => {
        setFilterObj({
            ...INITIAL_FILTER_STATE,
        })
        setCurrentPage(1)
        setPerPage(10)
        setFilterApplied(false)
        setData([])
        setTotalRows(0)
        setTotalPassengers(0)
    }

    const handleExportCSV = async () => {
        setExportLoading(true)
        try {
            const allData = []
            let offset = 0
            const batchSize = 100

            while (true) {
                const result = await getClicksReport({
                    variables: {
                        start_date: filterObj?.start_date,
                        end_date: filterObj?.end_date,
                        page: {
                            limit: batchSize,
                            offset,
                        },
                        item_category: filterObj?.item_category || null,
                        item_id: filterObj?.item_id > 0 ? filterObj.item_id : null,
                        countryCode: filterObj?.country_code || null,
                    },
                })

                if (result.data?.userItemClickReport?.success && result.data.userItemClickReport.code === '200') {
                    const batch = result.data.userItemClickReport.user_item_click_report
                    allData.push(...batch)

                    if (batch.length < batchSize) {
                        break
                    }
                    offset += batchSize
                } else {
                    throw new Error(result.data?.userItemClickReport?.message || 'Failed to fetch data')
                }
            }

            // Generate CSV
            const headers = [
                'Title',
                'Category Code',
                'Country',
                'Click Date',
                'Category Description',
                'Total Clicks',
                'Mobile Clicks',
                'Web Clicks',
                'Guest Clicks',
                'Registered Clicks',
            ]
            let csvContent = headers.join(',') + '\n'

            allData.forEach(row => {
                const title = i18next.language === 'ar' ? row.item_title?.ar : row.item_title?.en
                const category_description =
                    i18next.language === 'ar' ? row.category_description?.ar : row.category_description.en

                const values = [
                    `"${title || '-'}"`,
                    `"${row.category_code || '-'}"`,
                    `"${row.country_code || '-'}"`,
                    `"${row.click_date || '-'}"`,
                    `"${category_description || '-'}"`,
                    `"${row.total_clicks || 0}"`,
                    `"${row.mobile_clicks || 0}"`,
                    `"${row.web_clicks || 0}"`,
                    `"${row.guest_clicks || 0}"`,
                    `"${row.registered_clicks || 0}"`,
                ]
                csvContent += values.join(',') + '\n'
            })

            // Download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.setAttribute('href', url)
            link.setAttribute('download', `attraction-clicks-report-${moment().format('YYYY-MM-DD')}.csv`)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            handleError(error.message)
        } finally {
            setExportLoading(false)
        }
    }

    const columns = useMemo(() => [
        {
            name: t('Title'),
            selector: row => row.item_title?.en,
            sortable: true,
            cell: row => <div>{i18next.language === 'ar' ? row.item_title?.ar || '-' : row.item_title?.en || '-'}</div>,
            width: '180px',
        },
        {
            name: t('Category Code'),
            selector: row => row.category_code,
            sortable: true,
            cell: row => <div>{row.category_code || '-'}</div>,
            width: '150px',
        },
        {
            name: t('Country'),
            selector: row => row.country_code,
            sortable: true,
            cell: row => <div>{row.country_code || '-'}</div>,
            width: '150px',
        },
        {
            name: t('Click Date'),
            selector: row => row.click_date,
            sortable: true,
            cell: row => <div>{row.click_date ? moment(row.click_date).format('DD MMM YYYY') : '-'}</div>,
            width: '150px',
        },
        {
            name: t('Description'),
            selector: row => row.category_description?.en,
            sortable: true,
            cell: row => (
                <div>
                    {i18next.language === 'ar'
                        ? row.category_description?.ar || '-'
                        : row.category_description?.en || '-'}
                </div>
            ),
            width: '180px',
        },
        {
            name: t('Total Clicks'),
            selector: row => row.total_clicks,
            sortable: true,
            cell: row => <div>{row.total_clicks}</div>,
            width: '170px',
        },
        {
            name: t('Mobile Clicks'),
            selector: row => row.mobile_clicks,
            sortable: true,
            cell: row => <div>{row.mobile_clicks}</div>,
            width: '170px',
        },
        {
            name: t('Web Clicks'),
            selector: row => row.web_clicks,
            sortable: true,
            cell: row => <div>{row.web_clicks}</div>,
            width: '170px',
        },
        {
            name: t('Guest Clicks'),
            selector: row => row.guest_clicks,
            sortable: true,
            cell: row => <div>{row.guest_clicks}</div>,
            width: '170px',
        },
        {
            name: t('Registered Clicks'),
            selector: row => row.registered_clicks,
            sortable: true,
            cell: row => <div>{row.registered_clicks}</div>,
            width: '170px',
        },
    ])

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#f9fafb',
                fontWeight: 600,
                fontSize: '0.875rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto',
                textAlign: i18next.language === 'ar' ? 'right' : 'left',
            },
        },
        cells: {
            style: {
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto',
                textAlign: i18next.language === 'ar' ? 'right' : 'left',
            },
        },
        pagination: {
            style: {
                direction: i18next.language === 'ar' ? 'rtl' : 'ltr',
            },
            pageButtonsStyle: {
                direction: i18next.language === 'ar' ? 'rtl' : 'ltr',
                marginLeft: i18next.language === 'ar' ? '0' : '0.5rem',
                marginRight: i18next.language === 'ar' ? '0.5rem' : '0',
            },
        },
        noData: {
            style: {
                direction: i18next.language === 'ar' ? 'rtl' : 'ltr',
            },
        },
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1]">
                            <BuildingLibraryIcon className="w-[20px] h-[20px] text-[#285f74]" />
                        </div>
                        <div>
                            <span className="ms-3">{t('Attraction Clicks Summary')}</span>
                            <p className="ms-3 text-sm font-normal leading-6 text-gray-500">
                                {t('Provide the total number of services selected by tourists')}
                            </p>
                        </div>
                    </h1>

                    {/* Filters */}
                    <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('Travel Date Range')}</label>
                            <Flatpickr
                                options={{
                                    mode: 'range',
                                    dateFormat: 'Y-m-d',
                                    defaultDate: [
                                        moment().subtract(1, 'months').format('YYYY-MM-DD'),
                                        moment().format('YYYY-MM-DD'),
                                    ],
                                }}
                                value={[filterObj.start_date, filterObj.end_date]}
                                onChange={dates => {
                                    if (dates.length === 2) {
                                        setFilterObj({
                                            ...filterObj,
                                            start_date: moment(dates[0]).format('YYYY-MM-DD'),
                                            end_date: moment(dates[1]).format('YYYY-MM-DD'),
                                        })
                                    }
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Country')}</label>
                            <Select
                                value={filterObj.country_code || ''}
                                onChange={e =>
                                    setFilterObj({
                                        ...filterObj,
                                        country_code: e.target.value || '',
                                    })
                                }
                            >
                                <option value="">{t('Select')}</option>
                                {countries.map(country => (
                                    <option key={country.alpha_iso2} value={country.alpha_iso2}>
                                        {i18next.language === 'ar' ? country.country_ar : country.country}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('Category')}</label>
                            <Select
                                value={filterObj.item_category || ''} 
                                onChange={e =>
                                    setFilterObj({
                                        ...filterObj,
                                        item_category: e.target.value || '',
                                    })
                                }
                            >
                                <option value="">{t('Select')}</option>
                                {CATEGORY_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                        </div>
                    </div>
                </div>
            </div>

            <Divider className="my-4 mb-6" />

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={handleApplyFilter}
                        className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                        disabled={reportLoading}
                    >
                        {t('Filter')}
                    </button>
                    <button
                        type="button"
                        onClick={handleClearFilter}
                        className="inline-flex items-center justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        disabled={reportLoading}
                    >
                        {t('Clear')}
                    </button>
                </div>
                {filterApplied && data.length > 0 && (
                    <button
                        type="button"
                        onClick={handleExportCSV}
                        className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={exportLoading}
                    >
                        {exportLoading ? (
                            <>
                                <ArrowPathIcon className="-ml-0.5 mr-1.5 h-5 w-5 animate-spin" />
                                {t('Exporting...')}
                            </>
                        ) : (
                            <>
                                <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                {t('Export to CSV')}
                            </>
                        )}
                    </button>
                )}
            </div>

            {reportLoading ? (
                <div className="flex flex-col justify-center items-center bg-white h-96">
                    <img alt="loading" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
                </div>
            ) : (
                filterApplied && (
                    <>
                        {/* Summary Cards */}
                        {data.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                                {/* Total Records Card */}
                                <div className="bg-white p-5 rounded-lg shadow-lg border border-gray-100 flex items-center justify-between hover:shadow-xl transition-shadow duration-200">
                                    <div>
                                        <h3 className="text-gray-600 text-sm font-medium mb-1">{t('Total Records')}</h3>
                                        <p className="text-3xl font-bold text-primary">{totalRows}</p>
                                    </div>
                                    <div className="bg-primary/10 p-4 rounded-xl flex items-center justify-center">
                                        <DocumentTextIcon className="w-7 h-7 text-primary/90" />
                                    </div>
                                </div>
                            </div>
                        )}
                        <DataTable
                            columns={columns}
                            data={data}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationRowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            paginationDefaultPage={currentPage}
                            paginationPerPage={perPage}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            customStyles={customStyles}
                            noDataComponent={
                                <div className="text-center py-24 bg-gray-50 mt-0 rounded-lg w-full">
                                    <svg
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                        className="mx-auto h-12 w-12 text-gray-400"
                                    >
                                        <path
                                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                                            strokeWidth={2}
                                            vectorEffect="non-scaling-stroke"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <h3 className="mt-2 text-sm font-semibold text-gray-900">
                                        {t('No records found!')}
                                    </h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        {t('Please add or modify your search criteria and try again.')}
                                    </p>
                                    <div className="mt-6"></div>
                                </div>
                            }
                        />
                    </>
                )
            )}
        </>
    )
}
